import React, { useRef, useState } from "react";
import { Play, Pause, Music, X, Volume2, VolumeX } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import track from "./assets/fmexanthem.mp3";

export default function MusicPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const val = parseFloat(e.target.value);
    audioRef.current.currentTime = val;
    setProgress(val);
  };

  const handleVolume = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (audioRef.current) audioRef.current.volume = val;
    setMuted(val === 0);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    const newMuted = !muted;
    setMuted(newMuted);
    audioRef.current.volume = newMuted ? 0 : volume;
  };

  const handleEnded = () => setPlaying(false);

  const formatTime = (s) => {
    if (isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 left-6 z-50 bg-hotpink text-white p-3 rounded-full border-[3px] border-black shadow-[0_4px_0_#000]"
      >
        {open ? <X size={22} /> : <Music size={22} />}
      </motion.button>

      {/* Player Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ type: "spring", stiffness: 120, damping: 16 }}
            className="fixed bottom-20 left-6 z-40 w-80"
          >
            {/* Card */}
            <div className="bg-white border-[4px] border-black rounded-3xl shadow-[0_8px_0_#000] overflow-hidden">

              {/* Header bar */}
              <div className="bg-hotpink px-5 py-3 flex items-center justify-between border-b-[3px] border-black">
                <span className="font-darumadrop text-white text-xl tracking-wide">FMEX Player</span>
                <span className="text-white text-lg">🎵</span>
              </div>

              {/* Track info */}
              <div className="bg-fmexyellow border-b-[3px] border-black px-5 py-3">
                <div className="font-darumadrop text-black text-lg leading-tight">❤️‍🩹 Revenge Anthem</div>
                <div className="font-comfortaa text-xs text-black/60 font-bold mt-0.5">Play it while dumping your ex</div>
              </div>

              <div className="px-5 py-4 space-y-4">

                {/* Progress bar */}
                <div>
                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.1}
                    value={progress}
                    onChange={handleSeek}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #FF2D8A ${(progress / (duration || 1)) * 100}%, #e5e7eb ${(progress / (duration || 1)) * 100}%)`,
                      accentColor: "#FF2D8A",
                    }}
                  />
                  <div className="flex justify-between text-xs font-comfortaa font-bold text-gray-500 mt-1">
                    <span>{formatTime(progress)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>

                {/* Play button */}
                <div className="flex justify-center">
                  <motion.button
                    onClick={togglePlay}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-14 h-14 bg-hotpink text-white rounded-full border-[3px] border-black shadow-[0_4px_0_#000] flex items-center justify-center"
                  >
                    {playing ? <Pause size={24} /> : <Play size={24} />}
                  </motion.button>
                </div>

                {/* Volume control */}
                <div className="flex items-center gap-3">
                  <button onClick={toggleMute} className="text-black flex-shrink-0">
                    {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={muted ? 0 : volume}
                    onChange={handleVolume}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #FF5C00 ${(muted ? 0 : volume) * 100}%, #e5e7eb ${(muted ? 0 : volume) * 100}%)`,
                      accentColor: "#FF5C00",
                    }}
                  />
                </div>

              </div>

              {/* Footer */}
              <div className="bg-gray-50 border-t-[3px] border-black px-5 py-2 text-center font-darumadrop text-xs text-gray-400 tracking-widest uppercase">
                FMEX Walk of Fame 💥
              </div>

            </div>

            <audio
              ref={audioRef}
              src={track}
              preload="auto"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}